import { useEffect, useRef } from 'react';
import { Icon } from '../Icon.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  tagline?: string;
  accent: string;
  icon: (p: { s: number }) => React.ReactNode;
  /** When omitted, the footer button row is suppressed — picker-style sheets
   *  that close on selection don't need a primary action. */
  primaryLabel?: string;
  primaryDanger?: boolean;
  primaryDisabled?: boolean;
  onPrimary?: () => void;
  secondaryLabel?: string;
  children: React.ReactNode;
};

export function SheetShell({
  open,
  onClose,
  title,
  tagline,
  accent,
  icon,
  primaryLabel,
  primaryDanger,
  primaryDisabled,
  onPrimary,
  secondaryLabel = 'Cancel',
  children,
}: Props) {
  // Keep latest onClose in a ref so the effect only depends on `open` — otherwise
  // every parent re-render produces a new onClose, the effect cleanup runs, and the
  // body overflow toggle causes a visible flash mid-edit.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;
  const Icn = icon;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,14,10,0.45)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        animation: 'lt-fade-in .15s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-app)',
          borderRadius: '28px 28px 0 0',
          padding: '14px 18px 30px',
          maxWidth: 440,
          margin: '0 auto',
          width: '100%',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.25)',
          animation: 'lt-sheet-in .22s ease',
          maxHeight: 'calc(100dvh - 30px)',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            width: 38,
            height: 4,
            borderRadius: 2,
            background: 'var(--text-3)',
            opacity: 0.5,
            margin: '0 auto 14px',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 13,
              background: `color-mix(in oklab, var(--${accent}) 18%, transparent)`,
              color: `var(--${accent})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icn s={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="lt-serif"
              style={{
                fontSize: 22,
                fontWeight: 500,
                letterSpacing: -0.015,
                lineHeight: 1.1,
              }}
            >
              {title}
            </div>
            {tagline && (
              <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>{tagline}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon.x s={16} />
          </button>
        </div>

        {children}

        {primaryLabel && (
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button
              type="button"
              className="lt-btn lt-btn-soft"
              style={{ flex: '0 0 110px', padding: '13px 0' }}
              onClick={onClose}
            >
              {secondaryLabel}
            </button>
            <button
              type="button"
              className={primaryDanger ? 'lt-btn lt-btn-danger' : 'lt-btn lt-btn-primary'}
              style={{ flex: 1, padding: '13px 0' }}
              disabled={primaryDisabled}
              onClick={onPrimary}
            >
              {primaryLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// shared field primitives
export function FieldRow({
  label,
  value,
  hint,
  right,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  right?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        background: 'var(--bg-card)',
        borderRadius: 14,
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          {label}
        </div>
        <div
          className="lt-tabular"
          style={{ fontSize: 16, color: 'var(--text-1)', marginTop: 2, fontWeight: 500 }}
        >
          {value}
        </div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{hint}</div>}
      </div>
      {right}
    </div>
  );
}

export type SegOption<T extends string> = {
  id: T;
  label: string;
  color?: string;
  dot?: boolean;
};

export function SegRow<T extends string>({
  label,
  options,
  active,
  onChange,
}: {
  label: string;
  options: SegOption<T>[];
  active: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          marginBottom: 8,
          paddingLeft: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const on = o.id === active;
          const tint = o.color ? `var(--${o.color})` : 'var(--accent)';
          return (
            <button
              type="button"
              key={o.id}
              onClick={() => onChange(o.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: -0.005,
                background: on ? `color-mix(in oklab, ${tint} 20%, transparent)` : 'var(--bg-card)',
                border: on ? `1px solid ${tint}` : '1px solid var(--border)',
                color: on ? tint : 'var(--text-1)',
              }}
            >
              {o.dot && <span style={{ width: 8, height: 8, borderRadius: 999, background: tint }} />}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ToggleSwitch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      style={{
        width: 46,
        height: 28,
        borderRadius: 999,
        background: on ? 'var(--accent)' : 'var(--bg-sunken)',
        position: 'relative',
        transition: 'background .15s',
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 20 : 2,
          width: 24,
          height: 24,
          borderRadius: 999,
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}
